import { useMutation } from "@tanstack/react-query";
import { CheckBox, Text } from "@ui-kitten/components";
import AutoRenewalDisclosureModal from "components/AutoRenewalDisclosureModal";
import Button from "components/Button";
import Chevron from "components/Chevron";
import Container from "components/Container";
import Dialog from "components/Dialog";
import Footer from "components/Footer";
import FullScreenSpinner from "components/FullScreenSpinner";
import Header from "components/Header";
import HiddenView from "components/HiddenView";
import Input from "components/Input";
import MarketingConsentSection from "components/MarketingConsentSection";
import Spacer from "components/Spacer";
import {
  StripeTokenError,
  StripeTokenResponse,
} from "components/StripePaymentForm/StripePaymentForm";
import TenantLogo from "components/TenantLogo";
import dayjs from "dayjs";
import {
  createPrimaryUser,
  createShopPageAccount,
  resetBuyFlow,
  setIsEditingPrimaryUser,
  setPrimaryUser,
  setUserCreated,
} from "features/slices/buyFlowSlice";
import {
  resetShopPagePhaseTwo,
  setGroup,
  setStep,
} from "features/slices/shopPagePhaseTwoSlice";
import { PrimaryUser } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useAuth from "hooks/useAuth";
import useCurrentUser from "hooks/useCurrentUser";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSendConversionData from "hooks/useSendConversionData";
import useTenantUserFields from "hooks/useTenantUserFields";
import { getSanityImage } from "lib/sanity";
import { ShopScreenProps } from "navigation/types";
import { addSubscriptionFetcher } from "queries/account.queries";
import { useFetchAgreementsQuery } from "queries/agreement.queries";
import {
  useFetchFaqsQuery,
  useFetchShopPagePhaseTwoQuery,
} from "queries/cms.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { trackConversion } from "queries/conversion.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { createContactFetcher, updateUserFetcher } from "queries/user.queries";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { Pressable, ScrollView, View } from "react-native";
import eventBus from "utils/eventBus";
import { toDashboard, toGiftPage } from "utils/navigationUtils";
import { getShopEntryContext } from "utils/shopEntryContext";
import toast from "utils/toast";
import OrderSummary from "./components/OrderSummary";
import { planIdProperty, SEASON_TICKET_CHECKOUT_STEP } from "./helpers";
import HeroSection from "./HeroSection";
import SeasonTicketCheckoutFlow from "./SeasonTicketCheckoutFlow";
import StepFour from "./StepFour";
import StepOne from "./StepOne";
import StepThree from "./StepThree";
import StepTwo from "./StepTwo";

const isValidEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());

const ShopPhaseTwo = ({ route, navigation }: ShopScreenProps) => {
  const { step, signupCharges, selectedCmsPlan, quantity } = useAppSelector(
    (state) => state.shopPagePhaseTwo,
  );
  const { userCreated, selectedBillingInterval, loading, primaryUser } =
    useAppSelector((state) => state.buyFlow);
  const { slug } = route.params;

  const scrollRef = useRef<ScrollView>(null);
  const stepThreeRef = useRef<HTMLDivElement>();
  const [emailCaptureVisible, setEmailCaptureVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);
  const [pendingGroup, setPendingGroup] = useState<string | null>(null);
  const contactSubmitted = useRef(false);

  const { data: shopPage, isFetching: fetchingShopPage } =
    useFetchShopPagePhaseTwoQuery({ slug });
  const { data: faqs } = useFetchFaqsQuery({ slug, type: "shopPagesPhaseTwo" });
  const { data: branding } = useFetchTenantBrandingQuery();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const [emailConsent, setEmailConsent] = useState(
    tenantConfig?.emailMarketingConsentDefault ?? false,
  );
  const [smsConsent, setSmsConsent] = useState(
    tenantConfig?.smsMarketingConsentDefault ?? false,
  );
  const [autoRenewalConsentGiven, setAutoRenewalConsentGiven] = useState(false);
  const [autoRenewalModalVisible, setAutoRenewalModalVisible] = useState(false);
  const { data: autoRenewalAgreements } = useFetchAgreementsQuery(
    "AUTO_RENEWAL",
    selectedCmsPlan?.[planIdProperty],
    selectedBillingInterval,
  );

  const sendConversionData = useSendConversionData();
  const { mutateAsync: track } = useMutation(trackConversion);
  const { mutateAsync: createContact } = useMutation(createContactFetcher);
  const { mutateAsync: updateUser } = useMutation(updateUserFetcher);
  const { mutateAsync: addSubscription, isLoading: addingSubscription } =
    useMutation(addSubscriptionFetcher);
  const { tenantUserFields } = useTenantUserFields();
  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedCmsPlan?.[planIdProperty],
  );

  const planPackageSignupCharge = signupCharges.find(
    (charge) =>
      charge.billingInterval === selectedBillingInterval &&
      charge.planPackageId === selectedCmsPlan?.[planIdProperty],
  );

  const emailError = useMemo(() => {
    if (!emailTouched) return null;
    if (email.trim().length === 0) return "Email is required.";
    if (!isValidEmail(email)) return "Enter a valid email address.";
    return null;
  }, [email, emailTouched]);

  const dispatch = useAppDispatch();

  const { account, updateFetchAccountBffCache } = useAccount();
  const auth = useAuth();
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    if (auth.isLoggedIn) return;
    if (primaryUser.email) return;

    (async () => {
      const ctx = await getShopEntryContext();
      if (!ctx?.email) return;
      if (ctx.slug !== slug) return;
      dispatch(setPrimaryUser({ ...primaryUser, email: ctx.email }));
    })();
  }, [auth.isLoggedIn, dispatch, primaryUser, slug]);

  useEffect(() => {
    if (!primaryUser.email || capturedEmail) return;
    const normalizedEmail = primaryUser.email.trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) return;
    setEmail(normalizedEmail);
    setCapturedEmail(normalizedEmail);
  }, [capturedEmail, primaryUser.email]);

  useEffect(() => {
    if (!emailCaptureVisible) setEmailTouched(false);
  }, [emailCaptureVisible]);

  const getSeatMapUrl = useMemo(() => {
    if (!selectedCmsPlan) return undefined;
    const seatMapName = selectedCmsPlan.seatMapName;
    const assetRef = shopPage?.seatMaps?.find(
      (seatMap) => seatMap.name === seatMapName,
    )?.image.asset._ref;

    if (!assetRef) return undefined;

    return getSanityImage(assetRef);
  }, [selectedCmsPlan, shopPage]);

  const extractPlanPackageUserFields = (user: PrimaryUser) => {
    if (!planPackageUserFields || planPackageUserFields.length === 0) {
      return {};
    }

    const extracted: Record<string, any> = {};
    planPackageUserFields.forEach(({ field }) => {
      const value = user[field.name as keyof PrimaryUser];
      if (value !== undefined && value !== null && value !== "") {
        extracted[field.name] = value;
      }
    });
    return extracted;
  };

  const formatPlanPackageUserFields = (userFields: Record<string, any>) => {
    const formatted = { ...userFields };
    planPackageUserFields?.forEach(({ field }) => {
      if (field.type === "DATE" && formatted[field.name]) {
        formatted[field.name] = dayjs(formatted[field.name]).format(
          "YYYY-MM-DD",
        );
      } else if (field.type === "DATETIME" && formatted[field.name]) {
        formatted[field.name] = dayjs(formatted[field.name]).format(
          "YYYY-MM-DD HH:mm:ss",
        );
      }
    });
    return formatted;
  };

  const proceedToGroup = (groupName: string) => {
    if (!shopPage) return;
    const selectedGroup = shopPage.groups.find(
      (group) => group.name === groupName,
    );
    dispatch(setGroup(groupName));
    dispatch(
      setStep(
        selectedGroup?.seasonTicketPlan ? SEASON_TICKET_CHECKOUT_STEP : 2,
      ),
    );
    eventBus.emit("scroll-to-top");
  };

  const handlePlanGroupPurchase = (groupName: string) => {
    if (!shopPage) return;
    if (shopPage.emailGated && !capturedEmail) {
      if (auth.isLoggedIn) {
        const loggedInEmailCandidate = auth.username;
        const normalizedLoggedInEmail = loggedInEmailCandidate
          ?.trim()
          .toLowerCase();

        if (normalizedLoggedInEmail && isValidEmail(normalizedLoggedInEmail)) {
          if (!contactSubmitted.current) {
            contactSubmitted.current = true;
            void createContact({
              email: normalizedLoggedInEmail,
              source: "SHOP_PAGE_PHASE_TWO",
              buyflowStep: "Email Capture",
            }).catch(() => {
              contactSubmitted.current = false;
            });
          }
          setEmail(normalizedLoggedInEmail);
          setCapturedEmail(normalizedLoggedInEmail);
        }
        proceedToGroup(groupName);
        return;
      }
      setPendingGroup(groupName);
      setEmailCaptureVisible(true);
      return;
    }
    proceedToGroup(groupName);
  };

  const handleEmailCaptureConfirm = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) {
      setEmailTouched(true);
      return;
    }
    if (shopPage?.emailGated && !contactSubmitted.current) {
      contactSubmitted.current = true;
      void createContact({
        email: normalizedEmail,
        source: "SHOP_PAGE_PHASE_TWO",
        buyflowStep: "Email Capture",
      }).catch(() => {
        contactSubmitted.current = false;
      });
    }
    setCapturedEmail(normalizedEmail);
    setEmailCaptureVisible(false);
    if (pendingGroup) {
      proceedToGroup(pendingGroup);
      setPendingGroup(null);
    }
  };

  const createAccount = async (token?: StripeTokenResponse) => {
    if (auth.isLoggedIn) {
      if (!account || !auth.id || !selectedCmsPlan) {
        toast.error("Unable to add your subscription. Please try again.");
        return;
      }

      // Update user's plan package user fields before adding subscription
      const planPackageFields = extractPlanPackageUserFields(primaryUser);
      const formattedFields = formatPlanPackageUserFields(planPackageFields);

      if (Object.keys(formattedFields).length > 0 && currentUser) {
        try {
          await updateUser({
            userId: auth.id,
            user: {
              ...currentUser,
              ...formattedFields,
              userFields: {
                ...currentUser.userFields,
                ...formattedFields,
              },
            },
          });
        } catch (error) {
          toast.error("Failed to save user information. Please try again.");
          return;
        }
      }

      const updatedAccount = await addSubscription({
        billingInterval: selectedBillingInterval,
        userInfo: [
          {
            userId: auth.id,
            planPackageIds: new Array(quantity).fill(
              selectedCmsPlan[planIdProperty]!,
            ),
          },
        ],
        accountId: account.id,
        source: "BUY_FLOW",
        autoRenewalAgreementId: autoRenewalConsentGiven
          ? autoRenewalAgreements?.[0]?.id
          : undefined,
      });
      updateFetchAccountBffCache(updatedAccount);
      sendConversionData(
        planPackageSignupCharge!.signupCharge,
        quantity,
        token,
      );
      await track({
        step: "PURCHASE_COMPLETE",
        flow: "SHOP_PHASE_TWO",
        userId: auth.id,
      });
      dispatch(resetBuyFlow());
      dispatch(resetShopPagePhaseTwo());
      toDashboard();
      return;
    }

    let userCreateResult;
    // meaning user is making a checkout attempt the first time
    if (!userCreated) {
      // create primary user via /user/register endpoint
      userCreateResult = await dispatch(
        createPrimaryUser({
          tenantUserFields: tenantUserFields!,
          planPackageUserFields: planPackageUserFields!,
        }),
      ).unwrap();
      dispatch(setUserCreated(true));
    }
    // create account
    await dispatch(
      createShopPageAccount({
        billingToken: token,
        quantity,
        planPackageId: selectedCmsPlan![planIdProperty]!,
        tenantUserFields: tenantUserFields!,
        planPackageUserFields: planPackageUserFields!,
        emailMarketingConsent: emailConsent,
        smsMarketingConsent: smsConsent,
        autoRenewalAgreementId: autoRenewalConsentGiven
          ? autoRenewalAgreements?.[0]?.id
          : undefined,
      }),
    ).unwrap(); // Need this unwrap to throw error if create account fails

    sendConversionData(planPackageSignupCharge!.signupCharge, quantity, token!);
    await track({
      step: "PURCHASE_COMPLETE",
      flow: "SHOP_PHASE_TWO",
      userId: userCreated ? primaryUser.id : userCreateResult?.id,
    });
  };

  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse,
  ) => {
    if (error) {
      toast.error(error.error.message);
      return;
    }
    createAccount(response);
  };

  useEffect(() => {
    if (shopPage) {
      navigation.setOptions({ title: shopPage.name });
      ReactGA.send({ hitType: "pageview", page: shopPage.name });
      track({ step: "PAGE_LOAD", flow: "SHOP_PHASE_TWO" });
    }
  }, [shopPage]);

  // After user purchases a plan, redirect to dashboard
  useEffect(() => {
    if (!auth.isLoggedIn) return;
    if (auth.isLoggedIn && userCreated) {
      dispatch(resetBuyFlow());
      dispatch(resetShopPagePhaseTwo());
      toDashboard();
    }
  }, [auth, userCreated]);

  useEffect(() => {
    eventBus.on("scroll-to-top", () => {
      scrollRef.current?.scrollTo({
        y: 0,
        animated: true,
      });
    });
    return () => {
      eventBus.clear("scroll-to-top");
    };
  }, []);

  const scrollToNext = () => {
    // scroll to step three ref
    setTimeout(() => {
      stepThreeRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    });
  };

  if (fetchingShopPage)
    return (
      <View className=" h-screen w-screen grid place-items-center">
        <View className="animate-bounce">
          <TenantLogo />
        </View>
      </View>
    );

  if (!shopPage || branding?.subdomain !== shopPage.subdomain) {
    window.location.href = "/login";
    return null;
  }

  return (
    <ScrollView
      ref={scrollRef}
      className="flex"
      style={{ backgroundColor: "#f5f5f5" }}
    >
      {(loading || addingSubscription) && (
        <FullScreenSpinner
          text={
            addingSubscription
              ? "Adding your subscription..."
              : "Creating your account..."
          }
        />
      )}

      {/* Header */}
      <Header
        programName={shopPage.name}
        showLogin={false}
        textStyle={{
          color: branding?.secondaryColor,
        }}
      />
      <View className="flex-grow z-10">
        {step === 1 && (
          <>
            <HeroSection shopPage={shopPage} />
            <StepOne
              shopPage={shopPage}
              faqs={faqs}
              onPlanGroupPurchase={handlePlanGroupPurchase}
            />
          </>
        )}

        {step > 1 && (
          <Container className="relative pt-4 mb-16">
            <Pressable
              onPress={() => {
                dispatch(setStep(1));
                dispatch(setIsEditingPrimaryUser(true));
              }}
              className="mt-2 mb-6 flex-row gap-2 items-center w-max"
            >
              <Chevron direction="left" />
              <Text>Go Back</Text>
            </Pressable>
            {step === SEASON_TICKET_CHECKOUT_STEP && shopPage && (
              <SeasonTicketCheckoutFlow shopPage={shopPage} />
            )}
            {step > 1 && step !== SEASON_TICKET_CHECKOUT_STEP && (
              <View className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start relative">
                <View className="space-y-4">
                  <HiddenView shouldHide={step < 2}>
                    <StepTwo shopPage={shopPage} scrollToNext={scrollToNext} />
                  </HiddenView>
                  <HiddenView shouldHide={step < 3}>
                    <StepThree
                      ref={stepThreeRef}
                      createAccount={createAccount}
                    />
                  </HiddenView>
                  <HiddenView shouldHide={step < 4}>
                    <MarketingConsentSection
                      style={{ marginBottom: 24 }}
                      tenantName={branding?.name}
                      emailConsent={emailConsent}
                      smsConsent={smsConsent}
                      onEmailChange={setEmailConsent}
                      onSmsChange={setSmsConsent}
                    />
                  </HiddenView>
                  <HiddenView shouldHide={step < 4}>
                    {signupCharges.length > 0 && (
                      <StepFour
                        handleTokenReceived={handleTokenReceived}
                        emailGated={Boolean(shopPage?.emailGated)}
                        disabled={
                          autoRenewalAgreements &&
                          autoRenewalAgreements.length > 0 &&
                          !autoRenewalConsentGiven
                        }
                        beforeButton={
                          autoRenewalAgreements &&
                          autoRenewalAgreements.length > 0 ? (
                            <View style={{ marginBottom: 24 }}>
                              <AutoRenewalDisclosureModal
                                visible={autoRenewalModalVisible}
                                onClose={() =>
                                  setAutoRenewalModalVisible(false)
                                }
                                agreementBody={autoRenewalAgreements[0].body}
                              />
                              <CheckBox
                                testID="auto-renewal-checkbox"
                                checked={autoRenewalConsentGiven}
                                onChange={setAutoRenewalConsentGiven}
                              >
                                I have read and agreed to the auto renewal terms
                                of service
                              </CheckBox>
                              <Pressable
                                onPress={() => setAutoRenewalModalVisible(true)}
                                style={{ marginTop: 8 }}
                              >
                                <Text status="info">
                                  View auto-renewal terms
                                </Text>
                              </Pressable>
                            </View>
                          ) : undefined
                        }
                      />
                    )}
                  </HiddenView>
                </View>
                <View className="hidden md:block sticky top-2 space-y-10">
                  <img src={getSeatMapUrl} />
                  <OrderSummary />
                  {step === 2 && (
                    <Spacer>
                      <Button
                        testID="continue-button"
                        onPress={() => {
                          dispatch(setStep(step + 1));
                          scrollToNext();
                        }}
                        style={{
                          marginTop: 24,
                          marginBottom: 36,
                          width: 300,
                          alignSelf: "center",
                        }}
                      >
                        CONTINUE
                      </Button>
                      {selectedCmsPlan?.giftingEnabled && (
                        <a
                          onClick={() =>
                            toGiftPage(selectedCmsPlan[planIdProperty])
                          }
                          className="text-center cursor-pointer text-xl font-semibold"
                        >
                          Sending as a gift?
                        </a>
                      )}
                    </Spacer>
                  )}
                </View>
              </View>
            )}
          </Container>
        )}
      </View>
      <Footer />

      <Dialog
        visible={emailCaptureVisible}
        setVisible={setEmailCaptureVisible}
        cancelText="Back"
        okText="Continue"
        onCancel={() => {
          setEmailCaptureVisible(false);
          setPendingGroup(null);
        }}
        onOk={handleEmailCaptureConfirm}
        okBtnProps={{
          appearance: "filled",
          disabled: !isValidEmail(email),
          testID: "email-gate-continue-button",
        }}
        cancelBtnProps={{ appearance: "ghost" }}
        style={{ backgroundColor: "#ffffff", borderRadius: 20, padding: 24 }}
      >
        <View className="gap-2">
          <Text category="h5" style={{ color: "#0F172A" }}>
            Continue with email.
          </Text>
          <Text appearance="hint" style={{ color: branding?.secondaryColor }}>
            We’ll use this to save your progress.
          </Text>
          <View style={{ height: 6 }} />
          <Input
            label="Email address"
            placeholder="you@example.com"
            value={email}
            onChangeText={(value) => {
              setEmail(value);
              if (!emailTouched) setEmailTouched(true);
            }}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="email-address"
            onBlur={() => setEmailTouched(true)}
            onSubmitEditing={handleEmailCaptureConfirm}
            returnKeyType="go"
            testID="email-gate"
          />
          {!!emailError && <Text status="danger">{emailError}</Text>}
        </View>
      </Dialog>
    </ScrollView>
  );
};

export default ShopPhaseTwo;
